/**
 * Reorders applied-filter blocks so that blocks sharing an `orGroupKey` — i.e.
 * filters that OR together (the same logical attribute reached via different
 * entry points: a direct field, a rollup onto that field, the same field across
 * datasets) — render ADJACENTLY, never split apart by an unrelated filter.
 *
 * Stable: each OR-group is anchored at its FIRST member's position, and ungrouped
 * blocks keep their relative order — so nothing jumps around beyond pulling a
 * group's scattered members next to their first sibling.
 *
 * Display-only. The backend groups by the stamped `orGroupKey` regardless of
 * render order, so this never affects results.
 *
 * Annotates each block with:
 *   - orGroupSize : number of blocks in its OR-group (1 when ungrouped)
 *   - orGroupPos  : 0-based position within its OR-group
 * so the renderer can draw an "OR" connector between consecutive siblings
 * (connector after a block iff `orGroupPos < orGroupSize - 1`).
 *
 * @param {Array<{orGroupKey?: string}>} blocks
 * @returns {Array} reordered, annotated blocks
 */
export const clusterBlocksByOrGroup = (blocks) => {
  const order = []; // group keys, in first-seen order
  const groups = new Map(); // groupKey -> blocks[]
  let solo = 0;

  for (const block of blocks) {
    // Ungrouped blocks each get a unique synthetic key so they never merge with
    // one another (or with a real group).
    const groupKey = block.orGroupKey || `__solo_${solo++}__`;
    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
      order.push(groupKey);
    }
    groups.get(groupKey).push(block);
  }

  return order.flatMap((groupKey) => {
    const groupBlocks = groups.get(groupKey);
    return groupBlocks.map((block, i) => ({
      ...block,
      orGroupSize: groupBlocks.length,
      orGroupPos: i,
    }));
  });
};
